import {
  calculateFairValue,
  calculateFutureEarnings,
  calculateFutureMarketCapitalisation,
  calculateFutureRevenue,
  calculateFutureSharesOutstanding,
  calculateRevenueGrowthRate,
  calculateSharePriceFutureYear,
} from './FuturePEMultipleCalculation';
import type { NarrativeValuatorCompany } from '../../hooks/narrativeValuatorCompanyFragment';
import { getJsonField } from '@/utilities/object';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import { INPUT_VALUE_RANGES } from '../../utils/validation';
import {
  DEFAULT_DISCOUNT_RATE_PERCENTAGE,
  DEFAULT_TIME_PERIOD_IN_YEARS,
} from './constants';
import inRange from 'lodash/inRange';
import { getReportingCurrencyISO } from '@/utilities/getReportingCurrencyISO';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import {
  calculateListingToReportingExchangeRate,
  calculateReportingToListingExchangeRate,
  shouldConvertCurrency,
} from '../../utils/currencyConversion';
import { roundNumber, roundNumberToSignificant } from '../../utils/number';
import { FAIR_VALUE_PRECISION } from '../../utils/schema';
import type { FuturePEMultipleStrategy } from './FuturePEMultipleContext';
import {
  calculateSharesOutstandingGrowthRate,
  getCappedSharesOutstandingGrowthRate,
  getCappedSmallCapSharesOutstandingGrowthRate,
  isValidRangeOfSharesOutstandingGrowthRate,
  isValidRangeOfSmallCapSharesOutstandingGrowthRate,
} from './sharesOutstanding';
import { findMostRecentFiscalData } from './timeSeries';
import { subYears } from 'date-fns';

export class CreateNarrativeFuturePEMultipleCalculator
  implements FuturePEMultipleStrategy
{
  private _revenueYear0: number;
  private _revenueGrowthRatePercentage: number;
  private _profitMarginPercentage: number;
  private _futurePE: number;
  private _discountRatePercentage: number;
  private _presentSharesOutstanding: number;
  private _timeFrame: number;
  private _sharesOutstandingGrowthRate: number;
  private _company: NarrativeValuatorCompany;
  private _listingToReportingCurrencyExchangeRate: number;
  private _reportingToListingCurrencyExchangeRate: number;
  private _sharesOutstandingGrowthRate1Y: number;
  private _marketCapUSD: number;

  constructor(company: NarrativeValuatorCompany) {
    if (!company) {
      throw new Error('Must set a company');
    }

    this._company = company;

    // There's a case that if the company has not reported, such as a new company,
    // it will be impossible to calculate fair value, so we need to handle this case
    // from a product perspective
    this._revenueYear0 = this.getRevenueYear0();

    this._revenueGrowthRatePercentage =
      this.getRevenueGrowthRateOfAnalysts() * 100;
    this._timeFrame = this.getDefaultTimeFrame();

    this._profitMarginPercentage = this.isProfitable()
      ? this.getProfitMarginFromCompany() * 100
      : this.getIndustryProfitMargin() * 100;

    this._futurePE = this.isProfitable()
      ? this.getFuturePriceEarningsFromCompany()
      : this.getIndustryFuturePriceEarnings();

    this._presentSharesOutstanding = this.getSharesOutstandingFromCompany();

    const discountRatePercentage = this.getDiscountRateFromCompany() * 100;
    this._discountRatePercentage = this.isValidRangeOfDiscountRatePercentage(
      discountRatePercentage
    )
      ? discountRatePercentage
      : this.getDefaultDiscountRate();

    this._marketCapUSD = this.getMarketCapUSDFromCompany();

    this._sharesOutstandingGrowthRate1Y =
      this.getSharesOutstandingGrowthRate1Y();

    this._sharesOutstandingGrowthRate = this.getSharesOutstandingGrowthRate(
      company.timeSeries
    );

    this._reportingToListingCurrencyExchangeRate =
      this.getReportingToListingCurrencyExchangeRate();
    this._listingToReportingCurrencyExchangeRate =
      this.getListingToReportingCurrencyExchangeRate();
  }

  public get revenueYear0() {
    return this._revenueYear0;
  }

  public get revenueGrowthRatePercentage() {
    return this._revenueGrowthRatePercentage;
  }

  public get profitMarginPercentage() {
    return this._profitMarginPercentage;
  }

  public get futurePE() {
    return this._futurePE;
  }

  public get discountRatePercentage() {
    return this._discountRatePercentage;
  }

  public get timeFrame() {
    return this._timeFrame;
  }

  public get exchangeRate() {
    return this._reportingToListingCurrencyExchangeRate;
  }

  public get sharesOutstandingGrowthRate() {
    return this._sharesOutstandingGrowthRate;
  }

  public get futureSharesOutstanding() {
    return Math.round(
      calculateFutureSharesOutstanding({
        presentSharesOutstanding: this._presentSharesOutstanding,
        sharesOutstandingGrowthRate: this._sharesOutstandingGrowthRate,
        timeFrame: this._timeFrame,
      })
    );
  }

  public get futureEarnings() {
    return calculateFutureEarnings({
      futureRevenue: this._futureRevenue,
      profitMarginPercentage: this._profitMarginPercentage,
    });
  }

  public get futureMarketCapitalisation() {
    return calculateFutureMarketCapitalisation({
      futureEarnings: this.futureEarnings,
      futurePE: this._futurePE,
    });
  }

  public get sharePriceFutureYear() {
    return calculateSharePriceFutureYear({
      futureMarketCapitalisation: this.futureMarketCapitalisation,
      futureSharesOutstanding: this.futureSharesOutstanding,
    });
  }

  private getSharesOutstandingGrowthRate1Y() {
    const { analysisMisc } = this._company;

    const sharesOutstandingGrowthRate1Y =
      analysisMisc?.sharesOutstandingSingleGrowth1Y;

    return sharesOutstandingGrowthRate1Y ?? 0;
  }

  private get _futureRevenue() {
    return calculateFutureRevenue({
      revenueYear0: this._revenueYear0,
      revenueGrowthRatePercentage: this._revenueGrowthRatePercentage,
      timeFrame: this._timeFrame,
    });
  }

  private getSharesOutstandingGrowthRate(
    timeSeries: NarrativeValuatorCompany['timeSeries']
  ) {
    const mostRecentFiscalData = findMostRecentFiscalData(timeSeries);

    const endDate = mostRecentFiscalData
      ? new Date(mostRecentFiscalData.date)
      : new Date();
    const fiscalData12MonthsAgo = findMostRecentFiscalData(
      timeSeries,
      subYears(endDate, 1)
    );

    const hasNecessaryFiscalDataForSharesOutstandingGrowthRate =
      Array.from(timeSeries).length > 1 &&
      !isNil(mostRecentFiscalData) &&
      !isNil(fiscalData12MonthsAgo) &&
      !isNil(mostRecentFiscalData.totalSharesOutstanding) &&
      !isNil(fiscalData12MonthsAgo.totalSharesOutstanding) &&
      mostRecentFiscalData.totalSharesOutstanding !== 0 &&
      fiscalData12MonthsAgo.totalSharesOutstanding !== 0;

    const sharesOutstandingGrowthRate =
      hasNecessaryFiscalDataForSharesOutstandingGrowthRate
        ? calculateSharesOutstandingGrowthRate({
            sharesOutstanding: mostRecentFiscalData.totalSharesOutstanding ?? 0,
            previousYearSharesOutstanding:
              fiscalData12MonthsAgo.totalSharesOutstanding ?? 0,
          })
        : this._sharesOutstandingGrowthRate1Y;

    if (this.isAboveSmallCapThreshold()) {
      return isValidRangeOfSharesOutstandingGrowthRate(
        sharesOutstandingGrowthRate
      )
        ? sharesOutstandingGrowthRate
        : getCappedSharesOutstandingGrowthRate(sharesOutstandingGrowthRate);
    }

    return isValidRangeOfSmallCapSharesOutstandingGrowthRate(
      sharesOutstandingGrowthRate
    )
      ? sharesOutstandingGrowthRate
      : getCappedSmallCapSharesOutstandingGrowthRate(
          sharesOutstandingGrowthRate
        );
  }

  private isAboveSmallCapThreshold() {
    return this._marketCapUSD >= 1000000000;
  }

  private isProfitable() {
    const { analysisPast } = this._company;
    const { netIncome } = analysisPast ?? {};
    const isProfitable = Boolean(netIncome && netIncome > 0);

    return isProfitable;
  }

  private getRevenueYear0() {
    const { data } = this._company;
    const { currencyInfo } = data;
    const reportingUnit = getJsonField<number>(
      currencyInfo,
      'reporting_unit',
      isNumber
    );

    const mostRecentFiscalData = findMostRecentFiscalData(
      this._company.timeSeries
    );

    const revenue = mostRecentFiscalData?.revenue ?? 0;

    return typeof revenue === 'number' && reportingUnit
      ? roundNumber(revenue * reportingUnit, 2)
      : 0;
  }

  private getRevenueGrowthRateOfAnalysts() {
    const { analysisFuture, analysisPast } = this._company;
    const { revenueGrowthAnnual } = analysisFuture ?? {};
    const { revenueGrowth5y } = analysisPast ?? {};

    return typeof revenueGrowthAnnual === 'number'
      ? revenueGrowthAnnual
      : revenueGrowth5y ?? 0;
  }

  private getProfitMarginFromCompany() {
    const { analysisPast } = this._company;
    const { netIncomeMargin } = analysisPast ?? {};

    return typeof netIncomeMargin === 'number' ? netIncomeMargin : 0;
  }

  private getFuturePriceEarningsFromCompany() {
    const { analysisValue } = this._company;
    const { pe } = analysisValue ?? {}; // is actually the most current PE

    return pe ? pe : 0;
  }

  private getIndustryFuturePriceEarnings() {
    const { primaryIndustry, secondaryIndustry } = this._company;

    const primaryIndustryPE = primaryIndustry?.industryAverages?.find(
      ({ name }) => name === 'pe'
    )?.value;
    const secondaryIndustryPE = secondaryIndustry?.industryAverages?.find(
      ({ name }) => name === 'pe'
    )?.value;

    return secondaryIndustryPE || primaryIndustryPE || 0;
  }

  private getSharesOutstandingFromCompany() {
    const { data } = this._company;
    const { marketCap } = data;

    const sharesOutstanding = getJsonField<number>(
      marketCap,
      'shares_outstanding',
      isNumber
    );

    return sharesOutstanding !== null ? sharesOutstanding : 0;
  }

  private getDiscountRateFromCompany() {
    const { analysisValue } = this._company;
    const { intrinsicValue } = analysisValue ?? {};
    const costOfEquity = getJsonField<number>(
      intrinsicValue,
      'cost_of_equity',
      isNumber
    );

    return costOfEquity !== null ? costOfEquity : 0;
  }

  private isValidRangeOfDiscountRatePercentage(discountRatePercentage: number) {
    return inRange(
      discountRatePercentage,
      INPUT_VALUE_RANGES.discountRatePercentage.min,
      INPUT_VALUE_RANGES.discountRatePercentage.max + 1
    );
  }

  private getDefaultTimeFrame() {
    return DEFAULT_TIME_PERIOD_IN_YEARS;
  }

  private getDefaultDiscountRate() {
    return DEFAULT_DISCOUNT_RATE_PERCENTAGE;
  }

  private getIndustryAveragesMap() {
    const { primaryIndustry, secondaryIndustry } = this._company;

    const primaryIndustryAveragesMap = Object.fromEntries(
      primaryIndustry?.industryAverages?.map(({ name, value }) => [
        name,
        value,
      ]) ?? []
    );

    const secondaryIndustryAveragesMap = Object.fromEntries(
      secondaryIndustry?.industryAverages?.map(({ name, value }) => [
        name,
        value,
      ]) ?? []
    );

    return { primaryIndustryAveragesMap, secondaryIndustryAveragesMap };
  }

  private getIndustryProfitMargin() {
    const { primaryIndustryAveragesMap, secondaryIndustryAveragesMap } =
      this.getIndustryAveragesMap();

    const secondaryIndustryNetProfitMargin =
      secondaryIndustryAveragesMap['net_income_margin_1y'];
    const primaryIndustryNetProfitMargin =
      primaryIndustryAveragesMap['net_income_margin_1y'];

    return (
      secondaryIndustryNetProfitMargin || primaryIndustryNetProfitMargin || 0
    );
  }

  private getExchangeRateInfo() {
    const { data } = this._company;
    const { marketCap } = data;
    const reportedMarketCapitalisation =
      getJsonField<number>(marketCap, 'reported', isNumber) || 0;
    const listingMarketCapitalisation =
      getJsonField<number>(marketCap, 'listing', isNumber) || 0;
    const reportingCurrencyISO =
      getReportingCurrencyISO(data.currencyInfo) || 'XXX';
    const listingCurrencyISO =
      getTradingItemCurrencyISO(data.currencyInfo) || 'XXX';

    return {
      reportedMarketCapitalisation,
      listingMarketCapitalisation,
      reportingCurrencyISO,
      listingCurrencyISO,
    };
  }

  private getReportingToListingCurrencyExchangeRate() {
    const {
      reportedMarketCapitalisation,
      listingMarketCapitalisation,
      reportingCurrencyISO,
      listingCurrencyISO,
    } = this.getExchangeRateInfo();

    const exchangeRate = shouldConvertCurrency({
      listingCurrencyISO,
      reportingCurrencyISO,
    })
      ? calculateReportingToListingExchangeRate({
          listingMarketCapitalisation,
          reportedMarketCapitalisation,
        })
      : 1;

    return exchangeRate;
  }

  private getListingToReportingCurrencyExchangeRate() {
    const {
      reportedMarketCapitalisation,
      listingMarketCapitalisation,
      reportingCurrencyISO,
      listingCurrencyISO,
    } = this.getExchangeRateInfo();

    const exchangeRate = shouldConvertCurrency({
      listingCurrencyISO,
      reportingCurrencyISO,
    })
      ? calculateListingToReportingExchangeRate({
          listingMarketCapitalisation,
          reportedMarketCapitalisation,
        })
      : 1;

    return exchangeRate;
  }

  private getMarketCapUSDFromCompany() {
    const { analysisValue } = this._company;
    const { marketCapUSD } = analysisValue ?? {};
    return marketCapUSD ?? 0;
  }

  public calculateFairValue(): number {
    const data = {
      revenueYear0: this._revenueYear0,
      revenueGrowthRate: this._revenueGrowthRatePercentage,
      profitMargin: this._profitMarginPercentage,
      futurePE: this._futurePE,
      presentSharesOutstanding: this._presentSharesOutstanding,
      discountRate: this._discountRatePercentage,
      timeFrame: this._timeFrame,
      sharesOutstandingGrowthRate: this._sharesOutstandingGrowthRate,
    };

    return roundNumberToSignificant(
      calculateFairValue(data),
      FAIR_VALUE_PRECISION
    );
  }

  public setRevenueGrowthRate(revenueGrowthRatePercentage: number): this {
    this._revenueGrowthRatePercentage = revenueGrowthRatePercentage;
    return this;
  }

  public setProfitMargin(profitMarginPercentage: number): this {
    this._profitMarginPercentage = profitMarginPercentage;
    return this;
  }

  public setFuturePE(futurePE: number): this {
    this._futurePE = futurePE;
    return this;
  }

  public setDiscountRate(discountRatePercentage: number): this {
    this._discountRatePercentage = discountRatePercentage;

    return this;
  }

  public setTimeFrame(timeFrame: number): this {
    this._timeFrame = timeFrame;
    return this;
  }

  public setFairValue(fairValue: number): this {
    const {
      _timeFrame,
      _discountRatePercentage,
      _futurePE,
      _profitMarginPercentage,
      _revenueYear0,
      _presentSharesOutstanding,
      _sharesOutstandingGrowthRate,
    } = this;

    this._revenueGrowthRatePercentage = calculateRevenueGrowthRate({
      fairValue: fairValue * this._listingToReportingCurrencyExchangeRate,
      discountRate: _discountRatePercentage,
      profitMargin: _profitMarginPercentage,
      futurePE: _futurePE,
      revenueYear0: _revenueYear0,
      timeFrame: _timeFrame,
      sharesOutstanding: _presentSharesOutstanding,
      sharesOutstandingGrowthRate: _sharesOutstandingGrowthRate,
    });

    return this;
  }

  public calculateFairValueToListingCurrency(): number {
    return roundNumberToSignificant(
      this.calculateFairValue() * this._reportingToListingCurrencyExchangeRate,
      FAIR_VALUE_PRECISION
    );
  }
}
